import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import { H3, H4, H5, Icon, Link, Loader, P } from "@/base/components/Global";
import { Col, Row } from "@/base/components/Layout";
import {
  APOLLO_SIGN_IN_LOGO_ICON,
  GENERIC_LOCATION_ICON,
} from "@/constants/blobIcons";
import { useAppSelector } from "@/store/hooks";
import { selectRegions } from "@/store/region/selectors";
import { createUseStyles } from "@/theme";

import { Map } from "./Map";

type CountryName = "Estonia" | "Finland" | "Lithuania" | "Latvia";

const useStyles = createUseStyles(({ color, spacing, font }) => ({
  brandSubtitle: {
    "$isMobile &": {
      fontSize: font.size.s,
      padding: 0,
    },
  },
  territories: {
    width: 328,
    color: color.gray2,
    borderBottom: `1px solid ${color.gray5}`,
    paddingBottom: spacing.s,
    "& .activeCountryLink": {
      "& span": {
        color: color.black,
        "&:before": {
          position: "absolute",
          content: "''",
          bottom: -9,
          left: 0,
          width: "100%",
          height: 4,
          backgroundColor: color.primaryBrand,
        },
      },
    },
    "& a": {
      padding: [spacing.xs, 0],
      position: "relative",
    },
  },
  apolloIcon: {
    paddingLeft: spacing.l,
    display: "flex",
    flexGrow: 1,
  },
  clubCardIcon: {
    "$isDesktop &": {
      animation: "0.7s ease-out 0s 1 $slideInFromRight",
    },
    "$isMobile &": {
      animation: "0.8s cubic-bezier(.47,1.64,.41,.8) 0s 1 $rotateIn",
    },
    transformOrigin: "80% 70%",
    transform: "rotate(15deg)",
    filter: "drop-shadow(-4px 3px 14px rgba(0,0,0,0.28))",
  },
  "@keyframes slideInFromRight": {
    from: {
      transform: "translateX(200%) rotate(15deg)",
      opacity: 0,
    },
    to: {
      transform: "translateX(0) rotate(15deg)",
      opacity: 1,
    },
  },
  "@keyframes rotateIn": {
    from: {
      transform: "rotate(-40deg)",
      opacity: 0,
    },
    to: {
      transform: "rotate(15deg)",
      opacity: 1,
    },
  },
  content: {
    alignItems: "start",
    flexBasis: 0,
  },
  topRow: {
    marginBottom: spacing.xl,
  },
  link: {
    width: "100%",
    textAlign: "center",
  },
  header: {
    display: "flex",
    flexDirection: "column",
  },
}));

const navigate = (uri: string) => {
  setTimeout(() => {
    window.open(uri, "_self");
  }, 1000);
};

const RegionPage: React.FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [hoveredCountry, setHoveredCountry] = useState<CountryName>();
  const regions = useAppSelector(selectRegions);

  if (!regions) return <Loader />;

  return (
    <div>
      <Row noMargin>
        <Col col={12} directionColumn>
          <Row className={classes.topRow}>
            <Col col={12}>
              <div className={classes.header}>
                <H4 thin noMargin>
                  {t("no_region_title")}
                </H4>
                <H3 noMargin>{t("no_region_subtitle")}</H3>
                <H5 className={classes.brandSubtitle} thin brandColor>
                  {t("no_region_minititle")}
                </H5>
              </div>
              <div className={classes.apolloIcon}>
                <Icon
                  icon={APOLLO_SIGN_IN_LOGO_ICON}
                  className={classes.clubCardIcon}
                  width={106}
                  height={68}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col noMargin col={12} colMd={6} directionColumn>
              <Col noMargin col={12} className={classes.content}>
                <Col col={12} directionColumn noMargin>
                  <H5 prefixIcon={GENERIC_LOCATION_ICON} gray1 noMarginBottom>
                    {t("region_page_title")}
                  </H5>
                  <P noMarginTop small gray3>
                    {t("region_page_subtitle")}
                  </P>
                  <P>{t("region_page_content")}</P>
                  <P>{t("region_page_description")}</P>
                </Col>
              </Col>
            </Col>
            <Col noMargin col={12} colMd={6} alignCenter directionColumn>
              <Col
                col={12}
                className={classes.territories}
                alignCenter
                noMargin
              >
                {regions?.map((region) => {
                  return (
                    <Col
                      col={3}
                      noMargin
                      alignCenter
                      key={region.id}
                      className={classNames(
                        hoveredCountry === region.name && "activeCountryLink",
                      )}
                    >
                      <Link
                        gray4
                        block
                        className={classes.link}
                        action={() => navigate(region.domainName)}
                        onMouseEnter={() =>
                          setHoveredCountry(region.name as CountryName)
                        }
                        onMouseLeave={() => setHoveredCountry(undefined)}
                      >
                        {region.name}
                      </Link>
                    </Col>
                  );
                })}
              </Col>
              <Col col={12} directionColumn alignCenter>
                <Map
                  selectedRegion={hoveredCountry}
                  onHover={(region) => setHoveredCountry(region as CountryName)}
                />
              </Col>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default RegionPage;
